import { useState } from 'react'

import { type TransportLotRequest, type TransportLotResponse } from '../ds-model-api'

import type { ErrorResponse } from 'types/types'

export interface TransportLotResult {
  status: 'success' | 'error'
  message: string
  systemDate: string | null
  pageNo: number
  systemInfo: any | null
}

export interface TransportLotError {
  message: string
}

const validateRequest = (request: TransportLotRequest): string | null => {
  if (!request.recDate) return '入荷日は必須です。'
  if (!request.transports?.length) return '輸送手段は必須です。'
  if (!request.items?.length) return 'アイテムは必須です。'
  if (typeof request.minLoadCapacity !== 'number') return '積載量下限は必須です。'

  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  const dateStr = request.recDate instanceof Date 
    ? request.recDate.toISOString().split('T')[0]
    : request.recDate
  if (!dateRegex.test(dateStr)) {
    return '入荷日はYYYY-MM-DD形式で入力してください。'
  }

  // Validate transports
  const transportValidation = request.transports.find(transport => {
    if (!transport.transportTypeId) return true;
    if (typeof transport.maxWeight !== 'number' || transport.maxWeight <= 0) return true;
    if (typeof transport.maxPalletCount !== 'number' || transport.maxPalletCount <= 0) return true;
    return false;
  });
  
  if (transportValidation) {
    if (!transportValidation.transportTypeId) return '輸送手段IDは必須です。';
    if (typeof transportValidation.maxWeight !== 'number' || transportValidation.maxWeight <= 0) {
      return '最大重量は正の数で入力してください。';
    }
    if (typeof transportValidation.maxPalletCount !== 'number' || transportValidation.maxPalletCount <= 0) {
      return '最大パレット数は正の数で入力してください。';
    }
  }

  // Validate items
  const itemValidation = request.items.find(item => {
    if (!item.itemCode) return true;
    if (typeof item.orderQuantity !== 'number' || item.orderQuantity <= 0) return true;
    if (typeof item.weight !== 'number' || item.weight <= 0) return true;
    return false;
  });
  
  if (itemValidation) {
    if (!itemValidation.itemCode) return 'アイテムコードは必須です。';
    if (typeof itemValidation.orderQuantity !== 'number' || itemValidation.orderQuantity <= 0) {
      return '注文数量は正の数で入力してください。';
    }
    if (typeof itemValidation.weight !== 'number' || itemValidation.weight <= 0) {
      return '重量は正の数で入力してください。';
    }
    if (typeof itemValidation.maxPalletQuantity !== 'number' || itemValidation.maxPalletQuantity <= 0) {
      return '最大パレット数量は正の数で入力してください。';
    }
  }

  return null
}

export const useTransportLot = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<TransportLotResult | undefined>(undefined)
  const [transportLotData, setTransportLotData] = useState<TransportLotResponse | undefined>(undefined)

  const calculateTransportLot = async (request: TransportLotRequest, api: any): Promise<TransportLotResponse> => {
    setResult(undefined)
    setIsLoading(true)

    // Validate request
    const validationError = validateRequest(request)
    if (validationError) {
      const errorResult: TransportLotResult = {
        status: 'error',
        message: validationError,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      throw new Error(validationError)
    }

    try {
      const response = await api.calculateTransportLot({
        transportLotRequest: request
      })
      
      setTransportLotData(response)
      setResult({
        status: 'success',
        message: '積載計算が完了しました。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return response
    } catch (err) {
      const error = err as ErrorResponse
      const errorMessage = error.response?.status === 422 
        ? '入力値に誤りがあります。'
        : error.response?.statusText || '積載計算でエラーが発生しました。'
      const errorResult: TransportLotResult = {
        status: 'error',
        message: errorMessage,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    result,
    transportLotData,
    calculateTransportLot,
    isLoading,
  }
}